/**
 * Converts a color string (hex, rgb, rgba) to an RGBA array.
 */
export function colorStringToRgba(color: string): [number, number, number, number] | null {
    if (!color) return null;
    color = color.trim();

    // hex format: #fff or #ffffff
    if (color[0] === '#') {
        let hex = color.slice(1);
        if (hex.length === 3) {
            hex = hex
                .split('')
                .map((x) => x + x)
                .join('');
        }
        if (hex.length !== 6) return null;
        const num = parseInt(hex, 16);
        return [(num >> 16) & 255, (num >> 8) & 255, num & 255, 1];
    }

    // rgb or rgba format
    const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map((x) => x.trim());
        if (parts.length < 3) return null;
        const r = parseInt(parts[0], 10);
        const g = parseInt(parts[1], 10);
        const b = parseInt(parts[2], 10);
        const a = parts.length === 4 ? parseFloat(parts[3]) : 1;
        return [r, g, b, isNaN(a) ? 1 : a];
    }

    return null;
}

/**
 * Compares two color strings (hex, rgb, rgba) for equality by converting both to RGBA.
 */
export function areColorsEqual(color1: string, color2: string): boolean {
    const rgba1 = colorStringToRgba(color1);
    const rgba2 = colorStringToRgba(color2);
    if (!rgba1 || !rgba2) return false;
    // Compare with a tolerance for alpha channel.
    // This tolerance is added because floating-point alpha values might have minor precision differences
    // when parsed from different color formats (like rgba vs hex),
    // so treating them as equal within a small margin is more practical than requiring exact equality.
    return (
        rgba1[0] === rgba2[0] && rgba1[1] === rgba2[1] && rgba1[2] === rgba2[2] && Math.abs(rgba1[3] - rgba2[3]) < 0.01
    );
}
